import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import { usePermission } from '../../hooks/usePermission';
import { TallyExportService, TallyLedgerMapRow } from '../../services/dataExchange.service';
import { firstOfYearLocalIso, toLocalIsoDate } from '../../utils/dateUtils';

interface Draft { group: string; name: string; }

/** Tally Prime XML export: ledger masters, vouchers for a date range, and per-account group/name overrides. */
const TallyExport: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [rows, setRows] = useState<TallyLedgerMapRow[]>([]);
    const [filter, setFilter] = useState('');
    const [drafts, setDrafts] = useState<Record<string, Draft>>({});

    const load = useCallback(async () => {
        try {
            setRows(await TallyExportService.getLedgerMap());
        } catch (e) {
            toast.showApiError(e, 'Failed to load the ledger map');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const run = async (fn: () => Promise<void>, fail: string) => {
        try { await fn(); } catch (e) { toast.showApiError(e, fail); }
    };

    const draftOf = (r: TallyLedgerMapRow): Draft =>
        drafts[r.accountId] ?? { group: r.tallyGroupName ?? '', name: r.tallyLedgerName ?? '' };

    const save = (r: TallyLedgerMapRow) => run(async () => {
        const d = draftOf(r);
        if (!d.group.trim()) {
            toast.showError('Enter a Tally group');
            return;
        }
        await TallyExportService.saveOverride(r.accountId, d.group.trim(), d.name.trim());
        toast.showSuccess('Override saved');
        await load();
    }, 'Could not save the override');

    const clear = (r: TallyLedgerMapRow) => run(async () => {
        await TallyExportService.removeOverride(r.accountId);
        setDrafts(prev => { const n = { ...prev }; delete n[r.accountId]; return n; });
        await load();
    }, 'Could not remove the override');

    const shown = rows.filter(r => !filter.trim()
        || `${r.accountCode} ${r.accountName}`.toLowerCase().includes(filter.trim().toLowerCase()));

    return (
        <Box data-help-page="accounting.tallyExport.page" sx={{ p: { xs: 1, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>Tally Export</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
                Tally Prime import XML, BDT only (foreign-currency memo amounts are not exported). Import the masters file first,
                then the vouchers file. Verify against a Tally trial company before relying on it.
            </Alert>

            <Box data-help-section="accounting.tallyExport.controls" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
                <TextField data-help="accounting.tallyExport.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <TextField data-help="accounting.tallyExport.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <Button data-help="accounting.tallyExport.vouchers" variant="contained" startIcon={<FileDownloadIcon />}
                    onClick={() => run(() => TallyExportService.downloadVouchers(from, to), 'Voucher export failed')}>
                    Download vouchers (.xml)
                </Button>
                <Button data-help="accounting.tallyExport.masters" variant="outlined" startIcon={<FileDownloadIcon />}
                    onClick={() => run(() => TallyExportService.downloadMasters(), 'Masters export failed')}>
                    Download ledger masters (.xml)
                </Button>
            </Box>

            <Typography variant="h6" gutterBottom>Ledger group mapping</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                <TextField data-help="accounting.tallyExport.filter" label="Filter accounts" size="small" value={filter} onChange={e => setFilter(e.target.value)}
                    sx={{ flex: '1 1 260px', maxWidth: 360 }} />
            </Box>
            <TableContainer data-help-section="accounting.tallyExport.table" component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.tallyExport.col.account">Account</TableCell><TableCell data-help="accounting.tallyExport.col.default">Default Tally group</TableCell>
                            <TableCell data-help="accounting.tallyExport.col.group">Override group</TableCell><TableCell data-help="accounting.tallyExport.col.name">Override ledger name</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shown.map(r => (
                            <TableRow key={r.accountId}>
                                <TableCell>{r.accountCode} {r.accountName}</TableCell>
                                <TableCell>{r.defaultGroup}</TableCell>
                                <TableCell>
                                    <TextField data-help="accounting.tallyExport.row.group" size="small" disabled={!canManage} value={draftOf(r).group}
                                        inputProps={{ 'aria-label': `group ${r.accountCode}` }}
                                        onChange={e => setDrafts(p => ({ ...p, [r.accountId]: { ...draftOf(r), group: e.target.value } }))} />
                                </TableCell>
                                <TableCell>
                                    <TextField data-help="accounting.tallyExport.row.name" size="small" disabled={!canManage} value={draftOf(r).name}
                                        inputProps={{ 'aria-label': `name ${r.accountCode}` }}
                                        onChange={e => setDrafts(p => ({ ...p, [r.accountId]: { ...draftOf(r), name: e.target.value } }))} />
                                </TableCell>
                                <TableCell align="right">
                                    {canManage && <Button data-help="accounting.tallyExport.action.save" size="small" onClick={() => save(r)}>Save</Button>}
                                    {canManage && r.tallyGroupName && <Button data-help="accounting.tallyExport.action.clear" size="small" color="error" onClick={() => clear(r)}>Clear</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TallyExport;
